
import {BigNumber,logger,base58,safeDivide} from './../../imports/index.js';
// Given data
const solLamports: number = 1_000_000_000; // Known SOL decimals base unit
function readString(buffer: Buffer, offset: number): { value: string; newOffset: number } {
  // Ensure there are at least 4 bytes available for strLen
  if (offset + 4 > buffer.length) {
    throw new Error(`Not enough data to read string length at offset ${offset}`);
  }

  // Read the length (4 bytes, little-endian)
  const strLen = buffer.readUInt32LE(offset);
  offset += 4;

  // Ensure the string data is within buffer bounds
  if (offset + strLen > buffer.length) {
    throw new Error(`Not enough data to read string of length ${strLen} at offset ${offset}`);
  }

  // Read the string bytes
  const strBytes = buffer.slice(offset, offset + strLen);
  let value = strBytes.toString("utf-8");

  // Sanitize the string to remove null characters or invalid characters
  value = sanitizeString(value);

  offset += strLen;
  return { value, newOffset: offset };
}
export function isZero(obj:any){
  if (obj==0){
    return true
  }
  return false
}

/**
 * Sanitize a string by removing null bytes and trimming whitespace.
 */
function sanitizeString(input: string): string {
  if (!input) return input;
  return input.replace(/\u0000/g, "").trim(); // Remove null bytes and whitespace
}
/**
 * Calculates the UI price based on amount and decimals.
 */
function getUiPrice(amount: BigNumber, decimals: number): BigNumber | null {
  decimals = -Math.trunc(decimals);
  try {
    const uiPrice = amount.multipliedBy(new BigNumber(10).pow(decimals));
    return uiPrice;
  } catch (e) {
    console.error(`Error in getUiPrice: ${e}`);
    return null;
  }
}
export function getPrice(amount: any, decimals: number | null = null): number {
  if (amount === 0 || decimals === null) {
    // Handle zero amounts directly without decimal scaling
    return 0;
  }

  const bigAmount = new BigNumber(amount);

  if (decimals !== null) {
    const scale = new BigNumber(10).pow(decimals);
    return bigAmount.dividedBy(scale).toNumber();
  }

  return bigAmount.toNumber();
}
/**
 * Derives transaction price and appends metadata.
 */
export async function derive_transaction_price(
  data: any,
  solDecimals: number = 9,
  tokenDecimals: number = 6,
  invocation: number | null,
  consumerLogger: any = logger
) {
  let decodedData: any = null;

  try {
    decodedData = decodeTransactionData(data);

    if (
      !decodedData ||
      !decodedData.mint ||
      decodedData.sol_amount === null ||
      decodedData.token_amount === null ||
      !decodedData.user_address
    ) {
      consumerLogger.warn(`❌ Invalid decodedData: ${JSON.stringify(decodedData)}`);
      return null;
    }



    // Safely calculate price
    decodedData.price = safeDivide(decodedData.sol_amount, decodedData.token_amount);

    // Set invocation
    decodedData.invocation = invocation;
    return decodedData;
  } catch (error) {
    consumerLogger.error(`❌ Error decoding transaction price: ${error}`, { data });
    return null;
  }
}

export function decodeInstructionData(programData: string): Record<string, any> | null {
  const parsed: Record<string, any> = {};
  let offset = 0;

  try {
    // Base64 decode the input program data
    const decodedData = Buffer.from(programData, "base64");

    // Read discriminator (8 bytes)
    if (decodedData.length >= offset + 8) {
      const discriminator = decodedData.slice(offset, offset + 8);
      parsed["discriminator"] = discriminator.toString("hex");
      offset += 8;
    } else {
      throw new Error(`Not enough data to read discriminator at offset ${offset}`);
    }

    // Read 'name' field
    try {
      const result = readString(decodedData, offset);
      parsed["name"] = sanitizeString(result.value);
      offset = result.newOffset;
    } catch (error) {
      throw new Error(`Failed to decode 'name' field at offset ${offset}: ${error}`);
    }

    // Read 'symbol' field
    try {
      const result = readString(decodedData, offset);
      parsed["symbol"] = sanitizeString(result.value);
      offset = result.newOffset;
    } catch (error) {
      throw new Error(`Failed to decode 'symbol' field at offset ${offset}: ${error}`);
    }

    // Read 'uri' field
    try {
      const result = readString(decodedData, offset);
      parsed["uri"] = sanitizeString(result.value);
      offset = result.newOffset;
    } catch (error) {
      throw new Error(`Failed to decode 'uri' field at offset ${offset}: ${error}`);
    }

    // Read 'mint' public key (32 bytes)
    if (decodedData.length >= offset + 32) {
      const mintBytes = decodedData.slice(offset, offset + 32);
      parsed["mint"] = base58.encode(mintBytes);
      offset += 32;
    } else {
      throw new Error(`Not enough data to read 'mint' field at offset ${offset}`);
    }

    // Read 'bondingCurve' public key (32 bytes)
    if (decodedData.length >= offset + 32) {
      const bondingCurveBytes = decodedData.slice(offset, offset + 32);
      parsed["bonding_curve"] = base58.encode(bondingCurveBytes);
      offset += 32;
    } else {
      throw new Error(`Not enough data to read 'bondingCurve' field at offset ${offset}`);
    }

    // Read 'user' public key (32 bytes)
    if (decodedData.length >= offset + 32) {
      const userBytes = decodedData.slice(offset, offset + 32);
      parsed["user_address"] = base58.encode(userBytes);
      offset += 32;
    } else {
      throw new Error(`Not enough data to read 'user' field at offset ${offset}`);
    }

  } catch (error) {
    // Log detailed error information
    logger.error(`❌ Error decoding instruction data. Program Data: ${programData}`, {
      error: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : null,
    });
    return null;
  }

  return parsed;
}

/**
 * Decodes program data from a base64-encoded string.
 */
export function decodeTransactionData(program_data: string, decimals: any = null, all_js: any = {}): any | null {
  let decoded_data: Buffer;
  try {
    decoded_data = Buffer.from(program_data, 'base64');
  } catch (e) {
    console.error(`Error decoding base64 data: ${e}`);
    return null;
  }

  let offset = 8; // Adjusted offset to skip the first 8 bytes
  let parsed = { ...all_js };

  try {
    // Decode fields
    const mint = decoded_data.slice(offset, offset + 32);
    parsed['mint'] = base58.encode(mint);
    offset += 32;

    const solAmount = decoded_data.readBigUInt64LE(offset);
    parsed['sol_amount'] = getPrice(solAmount, 9); // Scale using 9 decimals
    offset += 8;

    const tokenAmount = decoded_data.readBigUInt64LE(offset);
    parsed['token_amount'] = getPrice(tokenAmount, 6); // Scale using 6 decimals
    offset += 8;

    const isBuy = decoded_data.readUInt8(offset) !== 0;
    parsed['is_buy'] = isBuy;
    offset += 1;

    const user = decoded_data.slice(offset, offset + 32);
    parsed['user_address'] = base58.encode(user);
    offset += 32;

    const timestamp = decoded_data.readBigInt64LE(offset);
    parsed['timestamp'] = Number(timestamp);
    offset += 8;

    const virtualSolReserves = decoded_data.readBigUInt64LE(offset);
    // Instead of storing it as BigInt in the object, store as string:
    parsed['virtual_sol_reserves'] = virtualSolReserves.toString();
    offset += 8;
    
    const virtualTokenReserves = decoded_data.readBigUInt64LE(offset);
    parsed['virtual_token_reserves'] = virtualTokenReserves.toString();
    offset += 8;

    // Calculate price safely
    parsed['price'] = safeDivide(parsed['sol_amount'], parsed['token_amount']); // Safe divide
  } catch (e) {
    console.error(`Error in decodeTransactionData: ${e}`);
    return null;
  }

  return parsed;
}
